import {Component, Inject, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {IDENTITY_EXTENSION_SERVICE, ZITI_DATA_SERVICE, ZitiDataService, ManagementPermissionsService} from "ziti-console-lib";
import {ApiService} from "@netfoundry-ui/shared/services";
import {cloneDeep, get, isEmpty} from "lodash";
import moment from 'moment';
import {IdentitiesExtensionService} from "../identities-extension/identities-extension.service";
import {firstValueFrom, Subscription} from "rxjs";
import {GrowlerData, GrowlerService} from "@netfoundry-ui/shared/growler";
import { ActivatedRoute, Router } from '@angular/router';

type IdentityTab = 'overview' | 'events' | 'metrics' | 'dial-logs' | 'visualizer';
const VALID_IDENTITY_TABS: IdentityTab[] = ['overview', 'events', 'metrics', 'dial-logs', 'visualizer'];

@Component({
    selector: 'netfoundry-ui-identity-form-extension',
    templateUrl: './identity-form-extension.component.html',
    styleUrls: ['./identity-form-extension.component.scss'],
    standalone: false
})
export class IdentityFormExtensionComponent implements OnInit, OnDestroy {
  private readonly conflictEntityLabel = 'identity';
  toggleOn = false;
  showToggle = false;

  subscription = new Subscription();

  enrollmentExpired = true;
  currentNetwork: any = {};
  jwtBasedAuthUrl: any = undefined;
  showJwtBasedAuth = false;
  conflictData: any = {};
  conflictDetails: any = {};
  conflictDetailsLoading = false;
  formData: any = {};
  useStrictReadonlyForm = false;
  attributeConflictDetails: any = {};
  attributeConflictWarning:
    | { roleAttributes: string[]; conflicted: boolean; resolvesExistingConflict: boolean; detailIdentityId?: string }
    | undefined;

  private currentTabParam: IdentityTab = 'overview';

  @ViewChild('identityForm') identityForm: any;
  constructor(
    @Inject(IDENTITY_EXTENSION_SERVICE) public svc: IdentitiesExtensionService,
    @Inject(ZITI_DATA_SERVICE) private zitiService: ZitiDataService,
    private apiService: ApiService,
    public growlerService: GrowlerService,
    private router: Router,
    private route: ActivatedRoute,
    private readonly managementPermissions: ManagementPermissionsService,
  ) {

  }

  ngOnInit() {
    this.refreshShareGate();
    this.subscription.add(
      this.managementPermissions.stateVersion$.subscribe(() => {
        this.refreshShareGate();
      }),
    );
    this.subscription.add(
      this.route.paramMap.subscribe((params) => {
        const tab = params.get('tab') as IdentityTab;
        this.currentTabParam = VALID_IDENTITY_TABS.includes(tab) ? tab : 'overview';
      })
    );
    this.subscription.add(
      this.svc.extensionEvent.subscribe((event: any) => {
        if (event?.type === 'formDataUpdated' && event?.data?.entityType === 'identity') {
          const nextFormData = event?.data?.formData || {};
          this.formData = nextFormData;
          this.attributeConflictWarning = undefined;
          this.attributeConflictDetails = {};
          this.enrollmentExpired =
            !nextFormData?.enrollment?.ott?.expiresAt || moment(nextFormData?.enrollment?.ott?.expiresAt).isBefore();
          this.getAuthPolicy();
          this.getConflictData();
          return;
        }

        if (event?.type === 'attributesChanged' && event?.data?.entityType === 'identity') {
          this.handleRoleAttributeInteraction({
            identityId: event?.data?.identityId,
            roleAttributes: event?.data?.roleAttributes || [],
          });
        }
      })
    );
    this.subscription.add(
      this.apiService.currentNetwork.subscribe((currentNetwork: any) => {
        this.currentNetwork = currentNetwork;
        if (currentNetwork?.networkController?.publicDomainName) {
          // use the alternate public domain name if it exists instead of the public domain name
          const publicDomainName = currentNetwork?.networkController?.alternatePublicDomainName ? currentNetwork?.networkController?.alternatePublicDomainName : currentNetwork?.networkController?.publicDomainName
          this.jwtBasedAuthUrl = `https://${publicDomainName}`;
        }
        this.getConflictData();
      })
    );
  }

  getAuthPolicy() {
    const filter = {
      filterName: 'id',
      columnId: 'id',
      value: this.svc?.formData?.authPolicy?.id,
      label: this.svc?.formData?.authPolicy?.id,
      type: 'TEXTINPUT',
      verb: '='
    };
    const paging = cloneDeep(this.zitiService.DEFAULT_PAGING);
    paging.noSearch = false;
    this.zitiService.get('auth-policies', paging, [filter]).then((result) => {
      const authPol = result?.data[0];
      if (authPol?.primary?.extJwt?.allowed) {
        this.showJwtBasedAuth = true;
      }
    });
  }

  getConflictData() {
    if (!this.currentNetwork?.id || !this.formData?.id) {
      this.conflictData = {};
      this.resetConflictDetails();
      this.updateConflictBadge();
      return;
    }
    firstValueFrom(this.svc.getIdentityConflicts(this.currentNetwork?.id, [this.svc.formData?.id])).then((result: any) => {
      this.conflictData = get(result, '_embedded.identityConflictList[0]');
      if (!this.conflictData?.conflicted) {
        this.resetConflictDetails();
      }
      this.updateConflictBadge();
      if (this.conflictData?.conflicted) {
        this.getConflictDetails(this.conflictData.identityId);
      }
    });
  }

  getConflictDetails(identityId?: string, roleAttributes: string[] = []) {
    const detailRoleAttributes = Array.isArray(roleAttributes) ? roleAttributes : [];
    const shouldUseAttributes = detailRoleAttributes.length > 0;
    const detailIdentityId = shouldUseAttributes
      ? ''
      : (identityId !== undefined
          ? identityId
          : (this.conflictData.identityId || this.formData?.id || ''));
    if (!this.currentNetwork?.id) {
      this.resetConflictDetails();
      return Promise.resolve({});
    }

    this.conflictDetailsLoading = true;
    return firstValueFrom(this.svc.getConflictDetails(this.currentNetwork?.id, detailIdentityId, detailRoleAttributes))
      .then((result: any) => {
        this.conflictDetails = result || {};
        this.svc.conflictDetails = this.conflictDetails;
        return this.conflictDetails;
      })
      .catch(() => {
        this.resetConflictDetails();
        return {};
      })
      .finally(() => {
        this.conflictDetailsLoading = false;
      });
  }

  handleRoleAttributeInteraction(change: any) {
    if (!this.currentNetwork?.id) {
      this.attributeConflictWarning = undefined;
      this.attributeConflictDetails = {};
      this.updateConflictBadge();
      return;
    }

    const roleAttributes = Array.isArray(change?.roleAttributes) ? change.roleAttributes : [];

    firstValueFrom(this.svc.getIdentityAttributeConflict(this.currentNetwork.id, roleAttributes, change.identityId))
      .then((result: any) => {
        const detailIdentityId = result?.identityId || change.identityId || this.formData?.id || '';

        if (result?.conflicted) {
          this.attributeConflictWarning = {
            roleAttributes: roleAttributes,
            conflicted: true,
            resolvesExistingConflict: false,
            detailIdentityId: detailIdentityId,
          };
          this.updateConflictBadge();

          this.getConflictDetails(undefined, roleAttributes).then((details: any) => {
            this.attributeConflictDetails = details || {};
          });
          return;
        }

        this.attributeConflictWarning = this.hasConflict ? {
          roleAttributes: roleAttributes,
          conflicted: false,
          resolvesExistingConflict: true,
          detailIdentityId: detailIdentityId,
        } : undefined;
        this.attributeConflictDetails = {};
        this.updateConflictBadge();
      })
      .catch(() => {
        this.attributeConflictWarning = undefined;
        this.attributeConflictDetails = {};
        this.updateConflictBadge();
      });
  }

  getAttributeConflictTooltip(attributeConflictWarning: { roleAttributes: string[]; conflicted: boolean; resolvesExistingConflict: boolean }) {
    if (attributeConflictWarning.resolvesExistingConflict) {
      return this.conflictResolutionMessage;
    }

    return attributeConflictWarning.conflicted ? this.previewConflictMessage : this.existingConflictMessage;
  }

  get conflictBannerMessage() {
    if (this.attributeConflictWarning?.resolvesExistingConflict) {
      return this.conflictResolutionMessage;
    }

    if (this.attributeConflictWarning?.conflicted) {
      return this.previewConflictMessage;
    }

    if (this.hasConflict) {
      return this.existingConflictMessage;
    }

    return '';
  }

  openConflictDetailsModal(event?: Event) {
    event?.preventDefault();
    event?.stopPropagation();

    if (!this.showConflictBanner || this.attributeConflictWarning?.resolvesExistingConflict) {
      return;
    }

    this.svc.conflictPageData = {
      title: 'Potential Conflicts',
      subtitle: this.conflictBannerMessage,
      conflictDetails: this.getModalConflictDetails(),
      roleAttributes: this.attributeConflictWarning?.roleAttributes || [],
      emptyStateMessage: this.genericEmptyStateMessage,
    };
    this.router.navigate(['/identities', this.entityId, 'conflict'], {
      state: {
        fromConflictSourceForm: true,
      },
    });
  }

  getModalConflictDetails() {
    if (this.attributeConflictWarning?.conflicted && this.attributeConflictDetails?.conflicts?.length > 0) {
      return this.attributeConflictDetails;
    }

    if (this.conflictDetails?.conflicts?.length > 0) {
      return this.conflictDetails;
    }

    return this.attributeConflictDetails || this.conflictDetails || {};
  }

  updateConflictBadge() {
    const activeFormData = this.formData || this.svc?.formData || {};
    if (!Array.isArray(activeFormData.badges)) {
      activeFormData.badges = [];
    }

    const hasConflictBadge = activeFormData.badges.some((badge: any) => badge.id === 'conflicted');
    if (!hasConflictBadge && this.shouldShowConflictBadge) {
      activeFormData.badges.push({label: 'Potential Conflict Detected', class: 'warning', id: 'conflicted'});
    } else if (hasConflictBadge && !this.shouldShowConflictBadge) {
      activeFormData.badges = activeFormData.badges.filter((badge: any) => {
        return badge.id !== 'conflicted';
      });
    }

    this.formData = activeFormData;
    this.svc.formData = activeFormData;
    this.syncBadgeBaseline(activeFormData.badges);
  }

  private syncBadgeBaseline(badges: any[]) {
    if (!this.identityForm?.initData) {
      return;
    }

    this.identityForm.initData.badges = cloneDeep(badges || []);
  }

  copyPublicDomainUrl() {
    navigator.clipboard.writeText(this.jwtBasedAuthUrl);
    this.growlerService.show(
      new GrowlerData('success', 'Success', 'Copied to clipboard', 'JWT based auth URL has been copied to your clipboard')
    );
  }

  resetConflictDetails() {
    this.conflictDetails = {};
    this.conflictDetailsLoading = false;
  }

  get showConflictBanner() {
    return this.hasConflict || !!this.attributeConflictWarning;
  }

  get hasConflict() {
    return this.conflictData?.conflicted;
  }

  get shouldShowConflictBadge() {
    return this.hasConflict && !this.attributeConflictWarning?.resolvesExistingConflict;
  }

  get genericConflictMessage() {
    return this.attributeConflictWarning?.conflicted ? this.previewConflictMessage : this.existingConflictMessage;
  }

  get existingConflictMessage() {
    return 'This identity may have conflicts.';
  }

  get previewConflictMessage() {
    return 'This identity may have potential conflicts. Saving these changes could introduce potential conflicts.';
  }
  
  get conflictResolutionMessage() {
    return `This identity has potential conflicts. Saving these changes may help resolve them.`;
  }
  
  get genericEmptyStateMessage() {
    return `This identity may have conflicts, but detailed information is not available at this time.`;
  }

  get hasEnrollmentJwt() {
    return !isEmpty(this.svc?.formData?.enrollment?.ott?.token);
  }

  get activeTab(): IdentityTab {
    return this.showEntityTabs ? this.currentTabParam : 'overview';
  }

  get entityId(): string {
    return this.route.snapshot.paramMap.get('id') || '';
  }

  get showEntityTabs() {
    return !isEmpty(this.svc?.formData?.id);
  }

  get entityTabData() {
    return {
      resourceType: 'endpoint',
      model: this.svc?.formData,
      networkId: this.currentNetwork?.id,
      networkGroupId: this.currentNetwork?.networkGroupId,
    };
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  private refreshShareGate(): void {
    this.useStrictReadonlyForm = this.managementPermissions.useStrictReadonlyFormUi('identities', false);
  }
}
